import React from 'react';
import { formatNumber } from './utils';
import type { StatCardConfig } from './types';
import type { AnalyticsData } from './types';

interface StatCardProps {
  config: StatCardConfig;
  value: number;
  trend: { value: number; positive: boolean } | null;
  miniChartData: number[];
}

function MiniChart({ dataPoints, color }: { dataPoints: number[]; color: string }) {
  const max = Math.max(...dataPoints, 1);
  return (
    <div className="flex items-end gap-px mt-0.5 h-2.5" aria-hidden>
      {dataPoints.map((value, index) => (
        <div
          key={index}
          className={`flex-1 min-w-0 ${color} rounded-t transition-all`}
          style={{ height: `${(value / max) * 100}%` }}
        />
      ))}
    </div>
  );
}

export function StatCard({ config, value, trend, miniChartData }: StatCardProps) {
  const Icon = config.icon;
  return (
    <div
      className={`bg-white p-1 border border-gray-200 rounded min-w-0 ${config.bgColor}`}
    >
      <div className="flex justify-between items-center gap-0.5 mb-0.5">
        <p className="font-medium text-[10px] text-gray-600 truncate leading-tight">
          {config.label}
        </p>
        <div className={`p-1 rounded bg-gradient-to-br ${config.color} text-white shrink-0`}>
          <Icon className="w-3 h-3" />
        </div>
      </div>

      <div className="flex flex-wrap items-baseline gap-1 mb-0.5">
        <p className="font-bold text-gray-900 text-xs truncate leading-tight">
          {formatNumber(value)}
        </p>
        {trend != null && (
          <span
            className={`text-[9px] font-medium px-1 py-0.5 rounded shrink-0 leading-none ${
              trend.positive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}
          >
            {trend.positive ? '↑' : '↓'}{trend.value.toFixed(0)}%
          </span>
        )}
      </div>

      <MiniChart dataPoints={miniChartData} color={config.barColor} />
    </div>
  );
}
